import React, { useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import {
  Alert, Badge, Button, Card, CardBody, CardTitle, Col, Container, Form, Input, Jumbotron, Label,
  Row, Spinner, Table,
} from 'reactstrap';
import moment from 'moment';
import { GET_KEEP_LINKS } from '../../query/query';
import history from '../../router/history';

// The URL segment for each search key, and the API argument it maps to.
const SEARCHES = {
  email: { arg: 'email', label: 'User email', placeholder: 'someone@example.com' },
  uid: { arg: 'uid', label: 'KEEP uid (NFC)', placeholder: '04A1B2C3D4E5F6' },
  pairing: { arg: 'pairingCode', label: 'Pairing code', placeholder: 'ABCD-1234' },
};

const formatDate = (date) => (date ? moment(date).format('DD/MM/YYYY HH:mm') : '—');

const KeepCard = ({ keep }) => (
  <Card className="mb-3">
    <CardBody>
      {/* Codes are read aloud to customers: a monospace font keeps O and 0 apart. */}
      <CardTitle tag="div" className="h5" style={{ fontFamily: 'inherit' }}>
        {'KEEP '}
        <code style={{ fontSize: '100%' }}>{keep.pairingCode}</code>
        <small className="text-muted">
          {'  uid '}
          <code>{keep.uid}</code>
          {` · ${keep.screenType || 'unknown screen'}`}
        </small>
      </CardTitle>
      {keep.pairedWithoutFactoryRegistration && (
        <Alert color="warning" className="py-2">
          {'Not registered by the factory: created when an owner or a sender first paired it on '}
          {formatDate(keep.pairedWithoutFactoryRegistrationAt)}
        </Alert>
      )}
      {keep.screenTypeHeal && (
        <Alert color="info" className="py-2">
          {`Screen type corrected from ${keep.screenTypeHeal.from} to ${keep.screenTypeHeal.to} on `}
          {formatDate(keep.screenTypeHeal.at)}
          {keep.screenTypeHeal.count > 1 && ` (×${keep.screenTypeHeal.count})`}
        </Alert>
      )}
      <Row className="mb-2">
        <Col md="2">{`${keep.messageCount} messages`}</Col>
        <Col md="2">{`${keep.syncedMessageCount} put on the KEEP`}</Col>
        <Col md="4" className="text-nowrap">{`Last message: ${formatDate(keep.lastMessageAt)}`}</Col>
        <Col md="4" className="text-nowrap">{`First sync: ${formatDate(keep.firstSyncedAt)}`}</Col>
      </Row>
      <Table size="sm" bordered>
        <thead>
          <tr>
            <th>Role</th>
            <th>Email</th>
            <th>Name</th>
            <th>Paired on</th>
          </tr>
        </thead>
        <tbody>
          {keep.pairedUsers.length === 0 && (
            <tr><td colSpan="4" className="text-muted">Nobody is paired on this KEEP.</td></tr>
          )}
          {keep.pairedUsers.map((user) => (
            <tr key={`${user.userId}-${user.role}`}>
              <td>
                <Badge color={user.role === 'OWNER' ? 'primary' : 'secondary'}>
                  {user.role === 'OWNER' ? 'Owner' : 'Sender'}
                </Badge>
              </td>
              <td>{user.email ? <a href={`/user/${user.email}`}>{user.email}</a> : <em>{user.userId}</em>}</td>
              <td>{user.name || ''}</td>
              <td>{formatDate(user.pairedAt)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <small className="text-muted">
        {`Created ${formatDate(keep.createdAt)}`}
        {keep.removedPairedUsersCount > 0 && ` · ${keep.removedPairedUsersCount} former owner(s) or sender(s)`}
      </small>
    </CardBody>
  </Card>
);

const KeepResults = ({ by, value }) => {
  const { data, loading, error } = useQuery(GET_KEEP_LINKS, {
    variables: { [SEARCHES[by].arg]: value },
    fetchPolicy: 'network-only',
  });
  if (loading) return <Spinner color="primary" />;
  if (error) return <Alert color="danger">{error.message}</Alert>;
  const keeps = (data && data.savGetKeepLinks) || [];
  if (keeps.length === 0) return <Alert color="warning">{`No KEEP found for ${value}.`}</Alert>;
  return keeps.map((keep) => <KeepCard key={keep._id} keep={keep} />);
};

const KeepInfoPage = ({ match: { params: { by: byProp, value: valueProp = '' } } }) => {
  const [by, setBy] = useState(byProp || 'email');
  const [value, setValue] = useState(decodeURIComponent(valueProp));
  // history is not the router's: pushing only updates the URL, so the search lives in state.
  const [searched, setSearched] = useState(
    byProp && valueProp ? { by: byProp, value: decodeURIComponent(valueProp) } : null,
  );

  const onSubmit = (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    history.push({ pathname: `/keep/${by}/${encodeURIComponent(trimmed)}` });
    setSearched({ by, value: trimmed });
  };

  return (
    <Container>
      <Jumbotron>
        <h3>KEEP info</h3>
        <hr className="my-2" />
        <Form onSubmit={onSubmit} className="mb-3">
          <Row form>
            <Col sm="3">
              <Label for="keep-search-by">Search by</Label>
              <Input type="select" id="keep-search-by" value={by} onChange={({ target }) => setBy(target.value)}>
                {Object.entries(SEARCHES).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </Input>
            </Col>
            <Col sm="6">
              <Label for="keep-search-value">&nbsp;</Label>
              <Input
                id="keep-search-value"
                value={value}
                placeholder={SEARCHES[by].placeholder}
                onChange={({ target }) => setValue(target.value)}
              />
            </Col>
            <Col sm="3" className="d-flex align-items-end">
              <Button color="primary" type="submit">Search</Button>
            </Col>
          </Row>
        </Form>
        {searched && <KeepResults by={searched.by} value={searched.value} />}
      </Jumbotron>
    </Container>
  );
};

export default KeepInfoPage;



// WEBPACK FOOTER //
// ./src/components/keep-info/keep-info-page.js